import React from 'react';
import { Button, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  SettingOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  StopOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import {
  TASK_STATUS_LABELS,
  TASK_STATUS_OPTIONS,
  TASK_TYPE_FOLLOWUP,
  TASK_TYPE_RECURRING,
  isOpenFollowup,
  isOpenRecurring,
  isTerminalStatus,
  normalizeTaskStatus,
  normalizeTaskType,
} from '../constants';
import { confirmDelete } from '../../../components/list/confirmDelete';
import { usePermission } from '../../permissions/PermissionsContext';
import type { TaskItemListItem, TaskItemStatus } from '../types';

const { Text } = Typography;

export interface TaskActivityRowActionsProps {
  record: TaskItemListItem;
  rowNumber: number;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: TaskItemStatus) => void;
  onResolve: (id: string) => void;
  onCompleteRecurring: (id: string) => void;
  onSnooze: (id: string) => void;
}

function statusMenuLabel(status: TaskItemStatus): string {
  return `Mark as ${TASK_STATUS_LABELS[status]}`;
}

const STATUS_MENU_ICONS: Record<TaskItemStatus, React.ReactNode> = {
  0: <PlayCircleOutlined />,
  1: <SyncOutlined />,
  2: <StopOutlined />,
  3: <CheckCircleOutlined />,
  4: <CloseCircleOutlined />,
};

const FOLLOWUP_WORKFLOW_STATUSES: TaskItemStatus[] = [0, 1, 2];

function appendStatusItem(
  target: MenuProps['items'],
  currentStatus: TaskItemStatus,
  nextStatus: TaskItemStatus,
  onSelect: () => void,
): void {
  target?.push({
    key: `status-${nextStatus}`,
    label: statusMenuLabel(nextStatus),
    icon: STATUS_MENU_ICONS[nextStatus],
    disabled: currentStatus === nextStatus,
    onClick: currentStatus === nextStatus ? undefined : onSelect,
  });
}

const TaskActivityRowActions: React.FC<TaskActivityRowActionsProps> = ({
  record,
  rowNumber,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onResolve,
  onCompleteRecurring,
  onSnooze,
}) => {
  const canEdit = usePermission('tasks.edit');
  const taskType = normalizeTaskType(record);
  const status = normalizeTaskStatus(record.status);

  const items: MenuProps['items'] = [
    {
      key: 'row-number',
      label: (
        <Text strong style={{ fontSize: 13 }}>
          #{rowNumber}
        </Text>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'view',
      label: 'View details',
      icon: <EyeOutlined />,
      onClick: () => onView(record.id),
    },
    ...(canEdit && !isTerminalStatus(status)
      ? [
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            onClick: () => onEdit(record.id),
          } as const,
        ]
      : []),
  ];

  if (canEdit) {
    const statusChildren: MenuProps['items'] = [];

    if (isTerminalStatus(status)) {
      appendStatusItem(statusChildren, status, 0, () => onStatusChange(record.id, 0));
    } else if (taskType === TASK_TYPE_FOLLOWUP) {
      for (const workflowStatus of FOLLOWUP_WORKFLOW_STATUSES) {
        appendStatusItem(statusChildren, status, workflowStatus, () =>
          onStatusChange(record.id, workflowStatus),
        );
      }
      if (isOpenFollowup(taskType, status)) {
        appendStatusItem(statusChildren, status, 3, () => onResolve(record.id));
        statusChildren?.push(
          {
            key: 'reschedule',
            label: 'Reschedule follow-up',
            icon: <EditOutlined />,
            onClick: () => onEdit(record.id),
          },
          {
            key: 'snooze',
            label: 'Snooze follow-up',
            icon: <ClockCircleOutlined />,
            onClick: () => onSnooze(record.id),
          },
        );
      }
    } else if (taskType === TASK_TYPE_RECURRING) {
      for (const o of TASK_STATUS_OPTIONS) {
        if (o.value === 3) continue;
        appendStatusItem(statusChildren, status, o.value, () =>
          onStatusChange(record.id, o.value),
        );
      }
      if (isOpenRecurring(taskType, status)) {
        appendStatusItem(statusChildren, status, 3, () => onCompleteRecurring(record.id));
      }
    } else {
      for (const o of TASK_STATUS_OPTIONS) {
        appendStatusItem(statusChildren, status, o.value, () =>
          onStatusChange(record.id, o.value),
        );
      }
    }

    if (statusChildren && statusChildren.length > 0) {
      items.push({ type: 'divider' });
      items.push({
        key: 'status-group',
        type: 'group',
        label: 'Set status',
        children: statusChildren,
      });
    }
  }

  if (canEdit) {
    items.push({ type: 'divider' });
    items.push({
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        confirmDelete({
          title: 'Delete activity',
          content: 'Are you sure you want to delete this? This cannot be undone.',
          onOk: () => onDelete(record.id),
        });
      },
    });
  }

  if (items.length === 1) {
    return (
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        onClick={() => onView(record.id)}
      />
    );
  }

  return (
    <Dropdown
      menu={{ items, onClick: ({ domEvent }) => domEvent.stopPropagation() }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
};

export default TaskActivityRowActions;
